import {AnalyticsDataWithTrends, AnalyticsData} from '../analytics.types';

/**
 * Flattens a nested object structure into a flat key-value object.
 * Nested objects are flattened using dot notation for keys.
 *
 * @param obj - The object to flatten
 * @param keyPrefix - Optional prefix for the flattened keys
 * @returns A flattened object with dot-notation keys
 */
function flattenObjectToAnalyticsData(obj: unknown, keyPrefix = ''): AnalyticsData {
  const flattenedData: AnalyticsData = {};

  if (!obj || typeof obj !== 'object' || obj === null) {
    return flattenedData;
  }

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const flattenedKey = keyPrefix ? `${keyPrefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively flatten nested objects
      const nestedFlattened = flattenObjectToAnalyticsData(value, flattenedKey);
      Object.assign(flattenedData, nestedFlattened);
    } else if (typeof value === 'string' || typeof value === 'number') {
      // Direct assignment for primitive values
      flattenedData[flattenedKey] = value;
    } else if (value !== null && value !== undefined) {
      // Convert other types to string (handles boolean, etc.)
      flattenedData[flattenedKey] = String(value);
    }
  }

  return flattenedData;
}

/**
 * Converts analytics data with trends structure to flattened analytics data format.
 * This function takes an array of AnalyticsDataWithTrends objects and converts them
 * to the flattened AnalyticsData format by combining dimensions and metrics.
 *
 * @param analyticsDataWithTrends - Array of analytics data with trends
 * @returns Array of flattened analytics data objects
 */
export default function convertDataWithTrendsToAnalyticsData(
  analyticsDataWithTrends: AnalyticsDataWithTrends[],
): AnalyticsData[] {
  if (!Array.isArray(analyticsDataWithTrends)) {
    return [];
  }

  return analyticsDataWithTrends.map((analyticsRow) => {
    const combinedData = {
      dimensions: analyticsRow.dimensions,
      metrics: analyticsRow.metrics,
    };

    return flattenObjectToAnalyticsData(combinedData);
  });
}
